import React, { useState } from 'react';
import { TeamInLeague, Player } from '../../domain/entities';
import { MAX_SQUAD_SIZE, YOUTH_ACADEMY_LEVELS } from '../../domain/constants';
import { Modal } from '../shared/Modal';
import { PlayerFace } from '../shared/PlayerFace';
import { useToast } from '../../contexts/ToastContext';
import { soundService } from '../../services/SoundService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSeedling, faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { AdPlaceholder } from '../shared/AdPlaceholder';
import { PotentialBadge } from '../shared/PotentialBadge';
import { FitnessBadge } from '../shared/FitnessBadge';
import { CollapsibleCard } from '../shared/CollapsibleCard';
import { YouthAcademyHelpModal } from '../shared/YouthAcademyHelpModal';
import './TrainingScreen.css';

interface YouthScreenProps {
    team: TeamInLeague;
    onUpgradeAcademy: () => Promise<{ success: boolean, message: string }>;
    onDowngradeAcademy: () => Promise<{ success: boolean, message: string }>;
    onPromotePlayer: (player: Player) => Promise<{ success: boolean, message: string }>;
    onUpdateYouthPreferences: (preferences: { roleFocus?: string; footPreference?: string; widthFocus?: string; playingStyle?: string; }) => void;
    leagueAverageSkill: number;
    adsEnabled: boolean;
    currentWeek: number;
}

export const YouthScreen = ({ team, onUpgradeAcademy, onDowngradeAcademy, onPromotePlayer, onUpdateYouthPreferences, leagueAverageSkill, adsEnabled, currentWeek }: YouthScreenProps) => {
    const { addToast } = useToast();
    const [modalAction, setModalAction] = useState<{ type: 'upgrade' | 'promote' | 'downgrade', player?: Player } | null>(null);
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [isPreferencesHelpOpen, setIsPreferencesHelpOpen] = useState(false);
    const [tempPreferences, setTempPreferences] = useState({
        roleFocus: team.youthAcademy.preferences?.roleFocus || 'Balanced',
        footPreference: team.youthAcademy.preferences?.footPreference || 'Balanced',
        widthFocus: team.youthAcademy.preferences?.widthFocus || 'Balanced',
        playingStyle: team.youthAcademy.preferences?.playingStyle || 'Balanced',
    });

    const academy = team.youthAcademy;
    const currentLevelInfo = YOUTH_ACADEMY_LEVELS[academy.level - 1];
    const nextLevelInfo = YOUTH_ACADEMY_LEVELS[academy.level] || null;

    const isSquadFull = team.squad.length >= MAX_SQUAD_SIZE;

    const flooredWeek = Math.floor(currentWeek);
    const nextIntakeWeek = Math.ceil(flooredWeek / 4) * 4;
    const weeksUntilIntake = nextIntakeWeek - flooredWeek;

    let intakeMessage: string;
    if (weeksUntilIntake === 0) {
        intakeMessage = "A new youth intake will arrive after this week's matches.";
    } else {
        const plural = weeksUntilIntake > 1 ? 's' : '';
        intakeMessage = `Next youth intake is in ${weeksUntilIntake} week${plural} (Week ${nextIntakeWeek}).`;
    }

    // Calculate preference effectiveness
    const preferenceEffectiveness = academy.preferences?.preferencesSetWeek !== undefined
        ? Math.max(0.25, (nextIntakeWeek - flooredWeek) / 4)
        : 1.0;
    const effectivenessPercent = Math.round(preferenceEffectiveness * 100);

    const handleAction = async () => {
        if (!modalAction) return;
        
        let result;
        if (modalAction.type === 'upgrade') {
            result = await onUpgradeAcademy();
        } else if (modalAction.type === 'promote') {
            result = await onPromotePlayer(modalAction.player!);
        } else { // downgrade
            result = await onDowngradeAcademy();
        }

        if (result.success) {
            soundService.playSuccess();
            addToast(result.message, 'success');
        } else {
            soundService.playError();
            addToast(result.message, 'error');
        }
        
        setModalAction(null);
    };

    const openModal = (type: 'upgrade' | 'promote' | 'downgrade', player?: Player) => {
        soundService.playClick();
        setModalAction({ type, player });
    }

    const handlePreferenceChange = (updatedPreferences: typeof tempPreferences) => {
        setTempPreferences(updatedPreferences);
        soundService.playClick();
        onUpdateYouthPreferences(updatedPreferences);
        addToast('Academy preferences updated!', 'success');
    };

    const sortedYouthSquad = [...academy.squad].sort((a,b) => b.skill - a.skill);

    const hasPreferencesSet = academy.preferences?.roleFocus !== undefined && academy.preferences.roleFocus !== 'Balanced' ||
        academy.preferences?.footPreference !== 'Balanced' ||
        academy.preferences?.widthFocus !== 'Balanced' ||
        academy.preferences?.playingStyle !== 'Balanced';

    return (
        <>
            <div className="main-content youth-screen">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h1><FontAwesomeIcon icon={faSeedling} style={{ marginRight: '8px', color: 'var(--positive-color)' }} />Youth Academy</h1>
                    <button
                        className="button"
                        onClick={() => setIsHelpOpen(true)}
                        style={{ padding: '8px 12px', fontSize: '0.9em' }}
                        title="Help"
                    >
                        Help
                    </button>
                </div>
                <p style={{ marginBottom: '24px', opacity: 0.9 }}>
                    Your academy provides a new intake of talent every 4 weeks. {intakeMessage}
                </p>

                {/* Current Academy Info */}
                <CollapsibleCard
                    cardId="youth-academy-info"
                    title={`${currentLevelInfo.name} - Level ${currentLevelInfo.level}`}
                    defaultCollapsed={false}
                >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                        <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                            <div style={{ fontSize: '0.9em', color: 'var(--accent-text-color)', marginBottom: '4px' }}>Weekly Cost</div>
                            <div style={{ fontSize: '1.4em', fontWeight: 'bold' }}>£{currentLevelInfo.weeklyWages.toLocaleString()}</div>
                            <div style={{ fontSize: '0.85em', marginTop: '4px' }}>Wages deducted each week</div>
                        </div>
                        <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                            <div style={{ fontSize: '0.9em', color: 'var(--accent-text-color)', marginBottom: '4px' }}>Current Level</div>
                            <div style={{ fontSize: '1.4em', fontWeight: 'bold' }}>{currentLevelInfo.level}</div>
                            <div style={{ fontSize: '0.85em', marginTop: '4px' }}>Youth prospect quality rating</div>
                        </div>
                    </div>

                    <div style={{ padding: '12px', backgroundColor: 'rgba(0, 255, 155, 0.1)', borderRadius: '4px', marginBottom: '16px' }}>
                        <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>What this academy provides:</p>
                        <ul style={{ margin: '0', paddingLeft: '20px' }}>
                            <li>Regular intakes of promising young talent every 4 weeks</li>
                            <li>Higher-skilled youth prospects as you increase the academy level</li>
                            <li>Talent scouting and development capabilities</li>
                            <li>Higher levels = better quality youth players & more potential stars</li>
                        </ul>
                    </div>

                    {/* Upgrade/Downgrade Controls */}
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                        {currentLevelInfo.level > 1 && (
                            <button
                                className="button"
                                onClick={() => openModal('downgrade')}
                                style={{ flex: 1 }}
                            >
                                <FontAwesomeIcon icon={faArrowDown} style={{ marginRight: '6px' }} />Downgrade to {YOUTH_ACADEMY_LEVELS[currentLevelInfo.level - 2]?.name || 'Previous Level'}
                            </button>
                        )}
                        {nextLevelInfo && (
                            <button
                                className="button"
                                onClick={() => openModal('upgrade')}
                                style={{ flex: 1 }}
                                disabled={team.finances.balance < nextLevelInfo.upgradeCost}
                            >
                                <FontAwesomeIcon icon={faArrowUp} style={{ marginRight: '6px' }} />Upgrade to {nextLevelInfo.name}
                            </button>
                        )}
                    </div>

                    {/* Upgrade/Downgrade Cost Info */}
                    {nextLevelInfo && (
                        <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px', fontSize: '0.9em', marginBottom: '12px' }}>
                            <strong>Upgrade Cost:</strong> £{nextLevelInfo.upgradeCost.toLocaleString()}
                            <div style={{ marginTop: '8px', fontSize: '0.85em', color: 'var(--accent-text-color)' }}>
                                Upgrading to {nextLevelInfo.name} increases weekly cost to £{nextLevelInfo.weeklyWages.toLocaleString()}/wk
                            </div>
                        </div>
                    )}
                    {currentLevelInfo.level > 1 && (
                        <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px', fontSize: '0.9em' }}>
                            <strong>💰 Save Weekly Wages:</strong> Downgrading reduces your weekly staff cost
                            <div style={{ marginTop: '8px', fontSize: '0.85em', color: 'var(--positive-color)' }}>
                                Downgrading to {YOUTH_ACADEMY_LEVELS[currentLevelInfo.level - 2]?.name} saves £{(currentLevelInfo.weeklyWages - YOUTH_ACADEMY_LEVELS[currentLevelInfo.level - 2]?.weeklyWages).toLocaleString()}/wk
                            </div>
                        </div>
                    )}
                </CollapsibleCard>

                {/* Youth Preferences Card */}
                <CollapsibleCard
                    cardId="youth-preferences"
                    title={`Academy Preferences ${hasPreferencesSet ? '✓' : ''}`}
                    defaultCollapsed={true}
                    headerActions={
                        <button
                            className="button"
                            onClick={() => setIsPreferencesHelpOpen(true)}
                            style={{ padding: '8px 12px', fontSize: '0.9em' }}
                            title="Learn about preferences"
                        >
                            Help
                        </button>
                    }
                >
                    <div style={{ marginBottom: '16px' }}>
                        <p style={{ margin: 0, marginBottom: '4px', opacity: 0.9 }}>Set recruitment preferences for your youth intake. Effectiveness: <strong>{effectivenessPercent}%</strong></p>
                        {weeksUntilIntake < 4 && weeksUntilIntake > 0 && <p style={{ margin: 0, fontSize: '0.85em', opacity: 0.7 }}>⏰ {weeksUntilIntake} week{weeksUntilIntake === 1 ? '' : 's'} until next intake</p>}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Role Focus</label>
                            <select value={tempPreferences.roleFocus} onChange={(e) => handlePreferenceChange({...tempPreferences, roleFocus: e.target.value})} style={{ width: '100%', padding: '8px' }}>
                                <option value="Balanced">Balanced - Mix of all positions</option>
                                <option value="Attacking">Attacking - More forwards & attacking mids</option>
                                <option value="Defensive">Defensive - More defenders & defensive mids</option>
                                <option value="Goalkeeper">Goalkeeper - Extra goalkeepers</option>
                            </select>
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Foot Preference</label>
                            <select value={tempPreferences.footPreference} onChange={(e) => handlePreferenceChange({...tempPreferences, footPreference: e.target.value})} style={{ width: '100%', padding: '8px' }}>
                                <option value="Balanced">Balanced - 50% Right, 50% Left</option>
                                <option value="Left-footed">Left-footed - 65% Left, 35% Right</option>
                                <option value="Right-footed">Right-footed - 75% Right, 25% Left</option>
                            </select>
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Width Focus</label>
                            <select value={tempPreferences.widthFocus} onChange={(e) => handlePreferenceChange({...tempPreferences, widthFocus: e.target.value})} style={{ width: '100%', padding: '8px' }}>
                                <option value="Balanced">Balanced - 50% Central, 50% Wide</option>
                                <option value="Central">Central - 70% Central, 30% Wide</option>
                                <option value="Wide">Wide - 70% Wide, 30% Central</option>
                            </select>
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Playing Style</label>
                            <select value={tempPreferences.playingStyle} onChange={(e) => handlePreferenceChange({...tempPreferences, playingStyle: e.target.value})} style={{ width: '100%', padding: '8px' }}>
                                <option value="Balanced">Balanced - Standard attributes</option>
                                <option value="Technical">Technical - Higher passing, creativity</option>
                                <option value="Pace">Pace - Higher pace, agility</option>
                                <option value="Physical">Physical - Higher strength, tackling</option>
                            </select>
                        </div>
                    </div>
                </CollapsibleCard>

                {adsEnabled && <AdPlaceholder />}

                <div className="panel">
                    <div className="panel-header">Youth Squad</div>
                <div className="table-wrapper">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Age</th>
                                <th>Pos</th>
                                <th>Skill</th>
                                <th>Fit</th>
                                <th>Potential</th>
                                <th>Wage</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedYouthSquad.length === 0 && (
                                <tr>
                                    <td colSpan={8} style={{ textAlign: 'center' }}>No youth players currently in the academy. New prospects will arrive soon.</td>
                                </tr>
                            )}
                            {sortedYouthSquad.map(player => (
                                <tr key={player.id}>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <PlayerFace player={player} teamColor={team.colors.primary} />
                                            {player.name}
                                        </div>
                                    </td>
                                    <td>{player.age}</td>
                                    <td>{player.position}</td>
                                    <td>{player.skill}</td>
                                    <td><FitnessBadge fitness={player.fitness} /></td>
                                    <td>
                                        <PotentialBadge 
                                            potential={player.potential} 
                                        />
                                    </td>
                                    <td>£{player.wage.toLocaleString()}/wk</td>
                                    <td style={{textAlign: 'right'}}>
                                        <button 
                                            className="button small"
                                            onClick={() => openModal('promote', player)}
                                            disabled={isSquadFull}
                                            title={isSquadFull ? `Cannot promote, senior squad is full (${MAX_SQUAD_SIZE} players).` : `Promote ${player.name} to senior squad.`}
                                            data-ai-target-promote-player-id={player.id}
                                        >
                                            Promote
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                </div>
            </div>

            {modalAction && (
                <Modal isOpen={!!modalAction} onClose={() => setModalAction(null)}>
                    {modalAction.type === 'upgrade' && nextLevelInfo && (
                        <>
                            <h2>Confirm Academy Upgrade</h2>
                            <p>Are you sure you want to upgrade your academy to Level {nextLevelInfo.level} for £{nextLevelInfo.upgradeCost.toLocaleString()}?</p>
                            <p className="text-secondary">A better academy will produce higher quality youth players.</p>
                            <div className="modal-actions">
                                <button className="button" onClick={() => setModalAction(null)}>Cancel</button>
                                {/* FIX: Removed argument from handleAction call. */}
                                <button className="button primary" onClick={handleAction}>Confirm Upgrade</button>
                            </div>
                        </>
                    )}
                     {modalAction.type === 'promote' && modalAction.player && (
                        <>
                            <h2>Confirm Promotion</h2>
                            <p>Are you sure you want to promote {modalAction.player.name} to the senior squad?</p>
                            <p>He will be added to your main team and will begin earning a weekly wage of £{modalAction.player.wage.toLocaleString()}.</p>
                            {isSquadFull && <p className="negative">Warning: Your senior squad is currently full. You will need to sell or dismiss a player to make room.</p>}
                            <div className="modal-actions">
                                <button className="button" onClick={() => setModalAction(null)}>Cancel</button>
                                {/* FIX: Removed argument from handleAction call. */}
                                <button className="button primary" onClick={handleAction} disabled={isSquadFull}>Confirm Promotion</button>
                            </div>
                        </>
                    )}
                    {modalAction.type === 'downgrade' && (
                         <>
                            <h2>Confirm Academy Downgrade</h2>
                            <p>Are you sure you want to downgrade your youth academy?</p>
                            <p className="negative">This will reduce weekly wage costs but will produce lower quality youth players. You will have to pay the upgrade fee again to return to this level.</p>
                            <div className="modal-actions">
                                <button className="button" onClick={() => setModalAction(null)}>Cancel</button>
                                {/* FIX: Removed argument from handleAction call. */}
                                <button className="button danger" onClick={handleAction}>Confirm</button>
                            </div>
                        </>
                    )}
                </Modal>
            )}

            <YouthAcademyHelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />

            <Modal isOpen={isPreferencesHelpOpen} onClose={() => setIsPreferencesHelpOpen(false)}>
                <h2>Academy Recruitment Preferences</h2>

                <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ marginBottom: '8px', color: 'var(--accent-color)' }}>📋 What are Preferences?</h3>
                    <p>Preferences guide youth generation but don't guarantee specific players. Think of them as recruitment instructions—they increase the odds of generating players matching your criteria. Effectiveness diminishes as the next intake approaches.</p>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ marginBottom: '8px', color: 'var(--accent-color)' }}>⏰ Timing Matters</h3>
                    <p>Set preferences well in advance of your next youth intake for maximum effectiveness (100%). Effectiveness is reduced if you change preferences close to the intake date. Each week brings diminishing returns as the intake approaches.</p>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ marginBottom: '8px', color: 'var(--accent-color)' }}>⚙️ Understanding Each Setting</h3>

                    <div style={{ marginBottom: '12px', paddingLeft: '12px', borderLeft: '3px solid var(--accent-color)' }}>
                        <strong>Role Focus:</strong>
                        <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                            <li><strong>Balanced:</strong> Mix of all positions</li>
                            <li><strong>Attacking:</strong> More forwards and attacking midfielders</li>
                            <li><strong>Defensive:</strong> More defenders and defensive midfielders</li>
                            <li><strong>Goalkeeper:</strong> Extra goalkeepers for your academy</li>
                        </ul>
                    </div>

                    <div style={{ marginBottom: '12px', paddingLeft: '12px', borderLeft: '3px solid var(--accent-color)' }}>
                        <strong>Foot Preference:</strong>
                        <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                            <li><strong>Balanced:</strong> 50% Right-footed, 50% Left-footed</li>
                            <li><strong>Left-footed:</strong> 65% Left-footed, 35% Right-footed</li>
                            <li><strong>Right-footed:</strong> 75% Right-footed, 25% Left-footed</li>
                        </ul>
                    </div>

                    <div style={{ marginBottom: '12px', paddingLeft: '12px', borderLeft: '3px solid var(--accent-color)' }}>
                        <strong>Width Focus:</strong>
                        <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                            <li><strong>Balanced:</strong> 50% Central, 50% Wide players</li>
                            <li><strong>Central:</strong> 70% Central, 30% Wide positions</li>
                            <li><strong>Wide:</strong> 70% Wide, 30% Central positions</li>
                        </ul>
                    </div>

                    <div style={{ marginBottom: '12px', paddingLeft: '12px', borderLeft: '3px solid var(--accent-color)' }}>
                        <strong>Playing Style:</strong>
                        <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                            <li><strong>Balanced:</strong> Standard attributes across the board</li>
                            <li><strong>Technical:</strong> Higher passing, creativity, and dribbling</li>
                            <li><strong>Pace:</strong> Higher pace, agility, and speed</li>
                            <li><strong>Physical:</strong> Higher strength, tackling, and heading</li>
                        </ul>
                    </div>
                </div>

                <div style={{ padding: '12px', backgroundColor: 'rgba(0, 255, 155, 0.1)', borderRadius: '4px', marginBottom: '16px' }}>
                    <strong>💡 Pro Tip:</strong> Set preferences early in your season to get the most out of your youth academy. Each intake brings several new prospects, so strategic preference setting can shape your youth recruitment over time.
                </div>

                <div className="modal-actions">
                    <button className="button primary" onClick={() => setIsPreferencesHelpOpen(false)}>Close</button>
                </div>
            </Modal>
        </>
    );
};
